import { CategorizedTags, TagModel } from '../../models';
import TagRepository from '../TagRepository';
import { apiFetch } from '../../utils/apiFetch';

export interface TagConflictError {
  message: string;
  existing_tag: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface TagModerationError {
  message: string;
  moderation_reason: string;
}

export class TagApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: TagConflictError | TagModerationError
  ) {
    super(message);
    this.name = 'TagApiError';
  }
}

export default class BeatAPITagRepository extends TagRepository {
  private _url: string;
  private _apiKey: string;

  constructor(url: string, apiKey: string) {
    super();
    this._url = url;
    this._apiKey = apiKey;
  }

  set url(url: string) {
    this._url = url;
  }

  set apiKey(apiKey: string) {
    this._apiKey = apiKey;
  }

  async getTags(): Promise<CategorizedTags> {
    const response = await apiFetch(this._url, {
      method: 'GET',
      apiKey: this._apiKey,
    });

    if (!response.ok) {
      throw new TagApiError(
        `Failed to fetch tags: ${response.statusText}`,
        response.status
      );
    }

    const data = await response.json();
    return this._transformCategorizedTags(data);
  }

  async createTag(name: string, token: string): Promise<TagModel> {
    const response = await apiFetch(this._url, {
      method: 'POST',
      body: JSON.stringify({ name }),
      apiKey: this._apiKey,
      authToken: token,
    });

    if (!response.ok) {
      const errorData = await response.json();

      if (response.status === 409) {
        throw new TagApiError('Tag already exists', 409, errorData as TagConflictError);
      }

      if (response.status === 422) {
        throw new TagApiError('Tag name failed moderation', 422, errorData as TagModerationError);
      }

      throw new TagApiError(
        errorData.message || `Failed to create tag: ${response.statusText}`,
        response.status
      );
    }

    const data = await response.json();
    return this._transformTag(data, true);
  }

  private _transformCategorizedTags(data: {
    system?: Array<{ id: string; name: string; slug: string }>;
    user?: Array<{ id: string; name: string; slug: string }>;
    general?: Array<{ id: string; name: string; slug: string }>;
    partner?: Array<{ id: string; name: string; slug: string }>;
  }): CategorizedTags {
    return {
      system: (data.system ?? []).map((t) => this._transformTag(t)),
      user: (data.user ?? []).map((t) => this._transformTag(t, true)),
      general: (data.general ?? []).map((t) => this._transformTag(t)),
      partner: (data.partner ?? []).map((t) => this._transformTag(t)),
    };
  }

  private _transformTag(
    data: {
      id: string;
      name: string;
      slug: string;
      user_created?: boolean;
      partner_id?: string | null;
    },
    userCreated: boolean = false
  ): TagModel {
    return new TagModel(
      data.id,
      data.name,
      data.slug,
      data.user_created ?? userCreated,
      data.partner_id ?? null
    );
  }
}
